import { Button, Divider, Menu, MenuItem } from '@mui/material';
import { Link2, Share2 } from 'lucide-react';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getFileBlob, getFileShareLink } from '../api/generateVideo.api';
import { SHARE_TARGETS } from '../shareTargets';
import type { VideoArtifact } from '../types';

/**
 * File sharing (Web Share API level 2) is only used on touch devices, where the OS sheet lists the
 * social apps. Desktop browsers (Safari, Chrome on macOS) expose it too, but their sheet only offers AirDrop /
 * Mail / Messages, so desktop gets the share-link menu instead.
 */
const canShareFiles =
  typeof navigator !== 'undefined' &&
  typeof navigator.canShare === 'function' &&
  typeof navigator.share === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

interface VideoShareButtonProps {
  artifact: VideoArtifact;
  prompt: string | null;
  disabled?: boolean;
}

/** Native share sheet with the video file when supported, else a menu of share links. */
export const VideoShareButton: React.FC<VideoShareButtonProps> = ({
  artifact,
  prompt,
  disabled,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const toast = {
    error: (message: string) => enqueueSnackbar(message, { variant: 'error' }),
    success: (message: string) => enqueueSnackbar(message, { variant: 'success' }),
  };
  const [busy, setBusy] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const text = prompt ?? '';

  const shareFile = async () => {
    setBusy(true);
    try {
      const blob = await getFileBlob(artifact.downloadUrl);
      const file = new File([blob], artifact.fileName, { type: blob.type || artifact.mimeType });
      if (!navigator.canShare({ files: [file] })) {
        toast.error(t('services.generateVideo.shareFailed'));
        return;
      }
      await navigator.share({ files: [file], text: text || undefined });
    } catch (error) {
      // AbortError = user closed the share sheet.
      if ((error as DOMException).name !== 'AbortError') {
        toast.error(t('services.generateVideo.shareFailed'));
      }
    } finally {
      setBusy(false);
    }
  };

  const withLink = async (action: (link: string) => void | Promise<void>) => {
    setBusy(true);
    try {
      const { url } = await getFileShareLink(artifact.publicId);
      setAnchor(null);
      await action(url);
    } catch {
      toast.error(t('services.generateVideo.shareFailed'));
    } finally {
      setBusy(false);
    }
  };

  const openTarget = async (buildUrl: (link: string, text: string) => string) => {
    // Opened synchronously on click: browsers block window.open after an await.
    const popup = window.open('about:blank', '_blank');
    if (popup) popup.opener = null;
    let navigated = false;
    try {
      await withLink((link) => {
        navigated = true;
        if (popup) popup.location.href = buildUrl(link, text);
        else window.location.assign(buildUrl(link, text));
      });
    } finally {
      // Cross-origin once navigated, so only close the still-blank window of a failed request.
      if (popup && !navigated) popup.close();
    }
  };

  const copyLink = () =>
    withLink(async (link) => {
      await navigator.clipboard.writeText(link);
      toast.success(t('services.generateVideo.linkCopied'));
    });

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<Share2 size={16} />}
        disabled={disabled || busy}
        onClick={(event) => (canShareFiles ? void shareFile() : setAnchor(event.currentTarget))}
      >
        {t('services.generateVideo.share')}
      </Button>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {SHARE_TARGETS.map((target) => (
          <MenuItem
            key={target.id}
            disabled={busy}
            onClick={() => void openTarget(target.buildUrl)}
          >
            {target.label}
          </MenuItem>
        ))}
        <Divider />
        <MenuItem disabled={busy} onClick={() => void copyLink()}>
          <Link2 size={16} className="mr-2" />
          {t('services.generateVideo.copyLink')}
        </MenuItem>
      </Menu>
    </>
  );
};
